import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  House, Users, ListChecks, ClipboardText, CalendarDots,
  Heartbeat, ChartBar, CurrencyDollar, Gear, SignOut, List, ShieldCheck, Buildings, UserGear, LockKey
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { useBranding } from "../lib/branding";
import { Button } from "./ui/button";
import UserMenu from "./UserMenu";

const NAV = [
  { to: "/admin/dashboard", label: "Platform", icon: ChartBar, roles: ["super_admin"] },
  { to: "/admin/tenants", label: "Tenants", icon: Buildings, roles: ["super_admin"] },
  { to: "/users", label: "Users", icon: UserGear, roles: ["super_admin"] },
  { to: "/admin/settings", label: "Settings", icon: Gear, roles: ["super_admin"] },
  { to: "/dashboard", label: "Home", icon: House, roles: ["owner","manager","hr","employee"] },
  { to: "/employees", label: "Employees", icon: Users, roles: ["owner","hr","manager"] },
  { to: "/templates", label: "KPI Templates", icon: ListChecks, roles: ["owner","hr"] },
  { to: "/evaluations", label: "Evaluations", icon: ClipboardText, roles: ["owner","hr","manager","employee"] },
  { to: "/calendar", label: "Calendar", icon: CalendarDots, roles: ["owner","hr","manager"] },
  { to: "/coaching", label: "Coaching", icon: Heartbeat, roles: ["owner","hr","manager"] },
  { to: "/analytics", label: "Analytics", icon: ChartBar, roles: ["owner","hr","manager"] },
  { to: "/bonus", label: "Bonus", icon: CurrencyDollar, roles: ["owner","hr"] },
  { to: "/audit-logs", label: "Audit Log", icon: ShieldCheck, roles: ["owner","hr"] },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["owner","hr"] },
];

const MOBILE_NAV = [
  { to: "/dashboard", label: "Home", icon: House },
  { to: "/employees", label: "Staff", icon: Users },
  { to: "/evaluations", label: "Evals", icon: ClipboardText },
  { to: "/analytics", label: "Stats", icon: ChartBar },
  { to: "/more", label: "More", icon: List },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileMenu, setMobileMenu] = useState(false);
  const role = user?.role;

  const visibleNav = NAV.filter((n) => n.roles.includes(role));

  return (
    <div className="min-h-screen bg-grain" data-testid="app-shell">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-[#E2E8F0] flex-col z-40">
        <div className="px-6 py-6 border-b border-[#E2E8F0]">
          <Link to="/dashboard" className="block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#0F172A] flex items-center justify-center overflow-hidden">
                <img src="/remediscore-logo.png" alt="RemediScore" className="w-8 h-8 object-contain" />
              </div>
              <div>
                <div className="font-display font-semibold text-lg text-[#0F172A]">{branding.company_display_name || "RemediScore"}</div>
                <div className="small-label">{branding.product_name?.includes("Platform") ? "Platform Console" : "Performance Suite"}</div>
              </div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-thin">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            const active = loc.pathname.startsWith(n.to);
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 transition-all
                  ${active
                    ? "bg-[#0EA5E9]/10 text-[#0EA5E9] font-medium"
                    : "text-[#475569] hover:bg-[#F1F5F9]"}`}
              >
                <Icon size={20} weight={active ? "duotone" : "regular"} />
                <span className="text-sm">{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-[#E2E8F0]">
          <UserMenu variant="sidebar" />
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0] px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0F172A] flex items-center justify-center overflow-hidden">
            <img src="/remediscore-logo.png" alt="RemediScore" className="w-6 h-6 object-contain" />
          </div>
          <span className="font-display font-semibold text-base">{branding.company_display_name || "RemediScore"}</span>
        </Link>
        <div className="flex items-center gap-2">
          <UserMenu variant="mobile" />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileMenu(!mobileMenu)}
            className="p-2 rounded-lg hover:bg-[#F1F5F9]"
          >
            <List size={22} />
          </button>
        </div>
      </header>

      {/* Mobile slide-out menu */}
      {mobileMenu && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenu(false)} />
          <div className="absolute top-0 right-0 bottom-0 w-72 bg-white shadow-xl flex flex-col">
            <div className="px-5 py-5 border-b border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center text-white font-semibold">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-base font-display font-semibold truncate" data-testid="mobile-user-name">{user?.name}</div>
                  <div className="text-xs text-[#475569] font-mono-data truncate" data-testid="mobile-user-email">{user?.email}</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="capitalize px-2 py-0.5 rounded-full bg-[#0EA5E9]/10 text-[#0EA5E9] font-medium" data-testid="mobile-user-role">
                  {user?.role?.replace("_", " ")}
                </span>
                <span className="text-[#475569] truncate ml-2" data-testid="mobile-user-tenant">
                  {user?.role === "super_admin" ? "Platform · All tenants" : (branding.company_display_name || "—")}
                </span>
              </div>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-3">
              {visibleNav.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setMobileMenu(false)}
                    data-testid={`mobile-nav-${n.to.replace("/", "")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-xl mb-1
                       ${isActive ? "bg-[#0EA5E9]/10 text-[#0EA5E9]" : "text-[#475569] hover:bg-[#F1F5F9]"}`
                    }
                  >
                    <Icon size={20} weight="duotone" />
                    <span className="text-sm font-medium">{n.label}</span>
                  </NavLink>
                );
              })}
            </nav>
            <div className="px-5 py-4 border-t border-[#E2E8F0] space-y-2"
                 style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
              <Button
                data-testid="mobile-change-password-button"
                variant="outline"
                className="w-full"
                onClick={() => { setMobileMenu(false); navigate("/change-password"); }}
              >
                <LockKey size={18} className="mr-2" /> Change password
              </Button>
              <Button
                data-testid="mobile-logout-button"
                variant="outline"
                className="w-full text-[#DC2626] hover:text-[#DC2626]"
                onClick={() => { logout(); navigate("/login"); }}
              >
                <SignOut size={18} className="mr-2" /> Log out
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="lg:pl-64 pb-24 lg:pb-12 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
          {children}
        </div>
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="bottom-nav lg:hidden" data-testid="bottom-nav">
        {MOBILE_NAV.map((n) => {
          const Icon = n.icon;
          const active = n.to !== "/more" && loc.pathname.startsWith(n.to);
          if (n.to === "/more") {
            return (
              <button
                key={n.to}
                onClick={() => setMobileMenu(true)}
                className="bottom-nav-item"
                data-testid="bottom-nav-more"
              >
                <Icon size={22} weight="regular" />
                <span>{n.label}</span>
              </button>
            );
          }
          return (
            <NavLink
              key={n.to}
              to={n.to}
              className={`bottom-nav-item ${active ? "active" : ""}`}
              data-testid={`bottom-nav-${n.to.replace("/", "")}`}
            >
              <Icon size={22} weight={active ? "duotone" : "regular"} />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
